import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import {
  FiCheck, FiShield, FiAlertCircle, FiPlus, FiEdit3,
  FiRefreshCw, FiSave, FiTrash2, FiX, FiBarChart2
} from 'react-icons/fi';
import { gatewayApi } from '../services/gateway';

const showToast = (message, type = 'success') => {
  window.dispatchEvent(new CustomEvent('show-toast', { detail: { message, type } }));
};

const blankPack = {
  id: '',
  name: '',
  price: 0,
  cycle: 'Monthly',
  monthlyAllowance: 0,
  annualAllowance: 0,
  warning: 80,
  block: 100,
  overage: 'BLOCK SENDING',
  status: 'active',
  notes: '',
  sortOrder: 0
};

const EmailPacks = () => {
  const navigate = useNavigate();
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [form, setForm] = useState(blankPack);

  const loadPacks = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await gatewayApi.listEmailPacks();
      setPacks(response.data || []);
    } catch (err) {
      setError(err.message || 'Unable to load email packs from PocketBase.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPacks();
  }, []);

  const activePacks = useMemo(
    () => packs.filter(pack => pack.status !== 'archived'),
    [packs]
  );

  const openCreate = () => {
    setForm({
      ...blankPack,
      sortOrder: packs.length + 1
    });
    setEditorOpen(true);
  };

  const openEdit = (pack) => {
    setForm({
      id: pack.id,
      name: pack.name || '',
      price: Number(pack.price || 0),
      cycle: pack.cycle || 'Monthly',
      monthlyAllowance: Number(pack.monthlyAllowance || 0),
      annualAllowance: Number(pack.annualAllowance || 0),
      warning: Number(pack.warning || 80),
      block: Number(pack.block || 100),
      overage: pack.overage || 'BLOCK SENDING',
      status: pack.status || 'active',
      notes: pack.notes || '',
      sortOrder: Number(pack.sortOrder || 0)
    });
    setEditorOpen(true);
  };

  const savePack = async () => {
    if (!form.name.trim()) {
      showToast('Pack name is required.', 'error');
      return;
    }
    if (Number(form.block) < Number(form.warning)) {
      showToast('Hard block percentage cannot be lower than the soft warning percentage.', 'error');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price) || 0,
        monthlyAllowance: Number(form.monthlyAllowance) || 0,
        annualAllowance: Number(form.annualAllowance) || 0,
        warning: Number(form.warning) || 0,
        block: Number(form.block) || 0,
        sortOrder: Number(form.sortOrder) || 0
      };

      if (form.id) {
        await gatewayApi.updateEmailPack(payload);
        showToast('Email pack updated in PocketBase.');
      } else {
        await gatewayApi.createEmailPack(payload);
        showToast('Email pack created in PocketBase.');
      }

      setEditorOpen(false);
      await loadPacks();
    } catch (err) {
      showToast(err.message || 'Unable to save email pack.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const deletePack = async () => {
    if (!form.id) return;
    if (!window.confirm(`Delete "${form.name}"? This removes the pack definition from PocketBase.`)) return;

    setSaving(true);
    try {
      await gatewayApi.deleteEmailPack(form.id);
      showToast('Email pack deleted.', 'warning');
      setEditorOpen(false);
      await loadPacks();
    } catch (err) {
      showToast(err.message || 'Unable to delete email pack.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-12">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Email Packs & Allowance</h2>
          <p className="text-sm text-slate-500">Live billing plans and quota policies stored in PocketBase.</p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={loadPacks}
            className="px-3 py-2.5 bg-white border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50"
            title="Refresh from PocketBase"
          >
            <SafeIcon icon={FiRefreshCw} />
          </button>
          <button
            onClick={openCreate}
            className="px-5 py-2.5 bg-slate-900 text-white text-sm font-bold rounded-xl shadow-md hover:bg-slate-800 transition-all flex items-center gap-2"
          >
            <SafeIcon icon={FiPlus} /> Create New Pack
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 flex gap-3 text-sm">
          <SafeIcon icon={FiAlertCircle} className="mt-0.5 shrink-0" />
          <div>
            <p className="font-bold">Email packs are not connected to the PocketBase runtime.</p>
            <p className="mt-1">{error}</p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-400">
          Loading email packs from PocketBase...
        </div>
      ) : activePacks.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-12 text-center">
          <p className="font-bold text-slate-800">No email packs configured.</p>
          <p className="text-sm text-slate-500 mt-2">Create your first pack and it will be saved to PocketBase.</p>
          <button onClick={openCreate} className="mt-5 px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-bold">
            Create First Pack
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {activePacks.map(pack => (
            <div key={pack.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col hover:border-blue-300 transition-all">
              <div className="p-6 border-b border-slate-100 bg-slate-50/50">
                <div className="flex justify-between gap-3 items-start">
                  <div>
                    <h3 className="text-lg font-bold text-slate-800">{pack.name}</h3>
                    <div className="flex items-baseline gap-1 mt-2">
                      <span className="text-2xl font-black text-slate-900">${Number(pack.price || 0).toLocaleString()}</span>
                      <span className="text-xs text-slate-500 font-medium">/ {pack.cycle || 'Monthly'}</span>
                    </div>
                  </div>
                  <span className="px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[9px] uppercase font-black">
                    {pack.status || 'active'}
                  </span>
                </div>
              </div>

              <div className="p-6 flex-1 space-y-4">
                <div className="space-y-2">
                  <PackLine value={Number(pack.monthlyAllowance || 0).toLocaleString()} text="emails / mo" />
                  <PackLine value={Number(pack.annualAllowance || 0).toLocaleString()} text="emails / yr" />
                  <PackLine value="Unlimited" text="Apps & Keys" />
                </div>

                <div className="pt-4 border-t border-slate-100 space-y-3">
                  <PolicyRow label="Soft Warning" value={`${pack.warning || 0}%`} tone="text-amber-600" />
                  <PolicyRow label="Hard Block" value={`${pack.block || 0}%`} tone="text-red-600" />
                  <PolicyRow label="Overage" value={pack.overage || 'Not set'} tone="text-slate-800" />
                </div>
              </div>

              <div className="p-4 bg-slate-50">
                <button
                  onClick={() => openEdit(pack)}
                  className="w-full py-2.5 bg-white border border-slate-200 text-slate-700 text-xs font-bold rounded-lg hover:border-blue-400 hover:text-blue-700 transition-all flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiEdit3} /> Edit Pack Details
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-slate-900 rounded-2xl p-8 text-white">
        <div className="flex flex-col lg:flex-row gap-8 items-center">
          <div className="flex-1 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-500/10 text-blue-400 rounded-full text-xs font-bold uppercase tracking-wider">
              <SafeIcon icon={FiShield} /> Global Policy
            </div>
            <h3 className="text-2xl font-bold">Automatic Overage Enforcement</h3>
            <p className="text-slate-400 text-sm leading-relaxed max-w-2xl">
              The gateway can enforce each API key and client allowance before an email is sent. Soft warning and hard block percentages are now stored with each email pack instead of being display-only values.
            </p>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                <span className="text-xs text-slate-300">Pack policies are editable</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                <span className="text-xs text-slate-300">Stored in PocketBase</span>
              </div>
            </div>
          </div>

          <div className="w-full lg:w-72 bg-white/5 rounded-xl p-6 border border-white/10 space-y-4">
            <SafeIcon icon={FiBarChart2} className="text-blue-400 text-2xl" />
            <div>
              <p className="text-sm font-bold">Usage & Load</p>
              <p className="text-[11px] text-slate-400 mt-1">View live email counts and allowance consumption from the Usage screen.</p>
            </div>
            <button
              onClick={() => navigate('/usage')}
              className="w-full py-2.5 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 transition-colors"
            >
              View Load Report
            </button>
          </div>
        </div>
      </div>

      {editorOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-3xl rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
            <div className="px-6 py-4 bg-slate-900 text-white flex justify-between items-center">
              <div>
                <h3 className="font-bold">{form.id ? 'Edit Email Pack' : 'Create Email Pack'}</h3>
                <p className="text-xs text-slate-400">Pricing and allowance policy saved to PocketBase.</p>
              </div>
              <button onClick={() => setEditorOpen(false)} className="p-1.5 hover:bg-slate-800 rounded-lg">
                <SafeIcon icon={FiX} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Pack Name *" value={form.name} onChange={value => setForm({ ...form, name: value })} />
                <Field label="Price" type="number" value={form.price} onChange={value => setForm({ ...form, price: value })} />
                <Select label="Billing Cycle" value={form.cycle} onChange={value => setForm({ ...form, cycle: value })}>
                  <option>Monthly</option>
                  <option>Annual</option>
                  <option>Custom</option>
                </Select>
                <Select label="Status" value={form.status} onChange={value => setForm({ ...form, status: value })}>
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                  <option value="archived">Archived</option>
                </Select>
                <Field label="Monthly Allowance" type="number" value={form.monthlyAllowance} onChange={value => setForm({ ...form, monthlyAllowance: value })} />
                <Field label="Annual Allowance" type="number" value={form.annualAllowance} onChange={value => setForm({ ...form, annualAllowance: value })} />
                <Field label="Soft Warning %" type="number" value={form.warning} onChange={value => setForm({ ...form, warning: value })} />
                <Field label="Hard Block %" type="number" value={form.block} onChange={value => setForm({ ...form, block: value })} />
                <Select label="Overage Behaviour" value={form.overage} onChange={value => setForm({ ...form, overage: value })}>
                  <option>BLOCK SENDING</option>
                  <option>ALLOW & FLAG</option>
                  <option>MANUAL UPGRADE</option>
                </Select>
                <Field label="Sort Order" type="number" value={form.sortOrder} onChange={value => setForm({ ...form, sortOrder: value })} />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-500 uppercase">Internal Notes</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="mt-2 w-full h-24 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:bg-white focus:border-blue-500"
                />
              </div>
            </div>

            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex flex-col-reverse sm:flex-row sm:justify-between gap-3">
              <div>
                {form.id && (
                  <button
                    onClick={deletePack}
                    disabled={saving}
                    className="px-4 py-2.5 text-sm font-bold text-red-600 hover:bg-red-50 rounded-xl flex items-center gap-2 disabled:opacity-50"
                  >
                    <SafeIcon icon={FiTrash2} /> Delete Pack
                  </button>
                )}
              </div>
              <div className="flex justify-end gap-3">
                <button onClick={() => setEditorOpen(false)} className="px-4 py-2.5 text-sm font-bold text-slate-500">Cancel</button>
                <button
                  onClick={savePack}
                  disabled={saving}
                  className="px-6 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-bold flex items-center gap-2 disabled:opacity-50"
                >
                  <SafeIcon icon={FiSave} /> {saving ? 'Saving...' : 'Save Pack'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PackLine = ({ value, text }) => (
  <div className="flex items-center gap-2 text-xs text-slate-600">
    <SafeIcon icon={FiCheck} className="text-emerald-500" />
    <span><b>{value}</b> {text}</span>
  </div>
);

const PolicyRow = ({ label, value, tone }) => (
  <div className="flex justify-between gap-4 text-[10px] font-bold uppercase text-slate-400">
    <span>{label}</span>
    <span className={tone}>{value}</span>
  </div>
);

const Field = ({ label, value, onChange, type = 'text' }) => (
  <div>
    <label className="text-xs font-bold text-slate-500 uppercase">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-2 w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:bg-white focus:border-blue-500"
    />
  </div>
);

const Select = ({ label, value, onChange, children }) => (
  <div>
    <label className="text-xs font-bold text-slate-500 uppercase">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-2 w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:bg-white focus:border-blue-500"
    >
      {children}
    </select>
  </div>
);

export default EmailPacks;